import axios from 'axios';
import { debugLog } from './debug';
import history from '../history';

/**
 * axiosSessionInterceptor — centralized session-expiry detection.
 *
 * The NVR backend returns HTTP 200 with result[1] === "noauth" when a session
 * is invalid or expired. This interceptor catches that pattern on ALL axios
 * responses so every screen (live, playback, setup, export) gets instant
 * session-expired detection without per-component plumbing.
 *
 * The interceptor dispatches EXPIRE_SESSION to Redux and fires a
 * "sessionexpired" DOM event (for non-Redux consumers like WebSocket players).
 *
 * Exclusions: login-flow methods (auth.loginUser*, auth.checkExists, etc.)
 * are exempt because "noauth" is a normal return value during login.
 */

// Methods where "noauth" is a normal/expected response, not a session expiry
const LOGIN_FLOW_METHODS = [
  'auth.loginUser',
  'auth.loginUserEnterprise',
  'auth.loginSession',
  'auth.loginSessionEnterprise',
  'auth.checkExists',
  'auth.checkHasAdmin',
  'auth.checkHasPlayback',
  'auth.logoutUser',
  'auth.logoutUserEnterprise',
  'config.meta.getStartup',
  'config.meta.getStartupLite',
  'info.isAlive',
  'info.getVersion',
  'info.getSerial',
];

/** Guard against multiple rapid-fire expiry dispatches */
let _expiring = false;
const EXPIRY_COOLDOWN_MS = 5000;

/**
 * Check whether a JSON-RPC response indicates session expiry.
 *
 * @param {object} responseData - axios response.data
 * @returns {boolean}
 */
export const isNoAuthResponse = (responseData) => {
  if (!responseData) return false;
  // Standard tuple pattern: result[1] === "noauth"
  if (Array.isArray(responseData.result) && responseData.result[1] === 'noauth') {
    return true;
  }
  // Bare string pattern (compat-2.5 style): result === "noauth"
  if (responseData.result === 'noauth') {
    return true;
  }
  return false;
};

/**
 * Extract the JSON-RPC method name from the request body.
 *
 * @param {*} requestData - axios config.data (string or object)
 * @returns {string|null}
 */
export const extractMethod = (requestData) => {
  if (!requestData) return null;
  // axios may have already parsed or it may still be a string
  const data = typeof requestData === 'string' ? JSON.parse(requestData) : requestData;
  return data?.method || null;
};

/**
 * Check if a method is exempt from session-expiry interception.
 *
 * @param {string} method - JSON-RPC method name
 * @returns {boolean}
 */
export const isExemptMethod = (method) => {
  if (!method) return false;
  return LOGIN_FLOW_METHODS.includes(method);
};

/**
 * Install the axios response interceptor.
 *
 * @param {object} store - Redux store (for dispatch)
 * @returns {number} interceptor ID (for removal in tests)
 */
export const installSessionInterceptor = (store) => {
  return axios.interceptors.response.use(
    (response) => {
      // Only inspect JSON-RPC POST responses
      if (response.config?.method !== 'post') return response;

      const data = response.data;
      if (!isNoAuthResponse(data)) return response;
      // A background request that handles its own noauth (the playback events prefetch, which also asks
      // eView members with no camera on screen) must not end the controller session (86bc8571y).
      if (response.config?.sessionExpiryExempt) return response;

      // Check if the method is exempt (login flow)
      let method = null;
      try {
        method = extractMethod(response.config.data);
      } catch (_) { /* non-JSON body, skip */ }

      if (isExemptMethod(method)) return response;

      // Session is expired — debounce and dispatch
      if (!_expiring) {
        _expiring = true;
        // Record WHAT tripped the expiry (the RPC method + target URL). This distinguishes a
        // genuine serving-session loss from a stray/transient noauth (e.g. an eView member NVR
        // call that should never have counted), and gives the modal a specific reason instead
        // of blanket-asserting "someone logged in from another device". (#86bb7phhh)
        const sessionEndReason = {
          cause: 'noauth',
          method: method || 'unknown',
          url: response.config?.url || 'unknown',
        };
        debugLog(`[session-interceptor] noauth → session expired — tripped by ${sessionEndReason.method} @ ${sessionEndReason.url}`);

        // Dispatch EXPIRE_SESSION to Redux
        const state = store.getState();
        const sSess = state.auth?.sSess || localStorage.getItem('sSess');

        // Import dynamically to avoid circular deps — expireSession is a thunk
        // We dispatch the action type directly and let the interceptor handle cleanup
        // This avoids the expireSession thunk making another axios call that
        // could re-trigger this interceptor.
        const { EXPIRE_SESSION } = require('../actions/types');
        const { persistor } = require('../store/store');
        const { clearStreamPrefs } = require('./clearStreamPrefs');
        const { clearJumpedServerUrl } = require('./getServerInfo');
        const { clearUserSelection } = require('./serverPersistence');
        const { getBackend } = require('../backend');

        localStorage.removeItem('sSess');
        localStorage.removeItem('eviewMode');
        clearStreamPrefs();
        clearJumpedServerUrl();
        clearUserSelection();
        // Synchronously drop the persisted root BEFORE the async purge so stale
        // server.serverSessions / auth can't rehydrate on re-login and feed a stale
        // session into the video-stream URLs (metadata ticks, but no frames).
        try { localStorage.removeItem('persist:root'); } catch (e) { /* ignore */ }
        persistor.purge();
        // Also drop the CloudNvrAdapter's persisted session (dl_cloud_session) so an
        // expiry-ended cloud session can't rehydrate account A into account B. Mirrors
        // logoutUser/expireSession. This interceptor is JSON-RPC-only (cloud is REST) so
        // it rarely fires in cloud, but the guard is cheap belt-and-suspenders and
        // no-op-safe for the local adapter (no clearToken).
        try { const be = getBackend(); if (be && be.clearToken) be.clearToken(); } catch (e) { /* ignore */ }

        store.dispatch({ type: EXPIRE_SESSION, reason: sessionEndReason });

        // Fire DOM event for non-Redux consumers (WS player, etc.) — carry the reason too
        window.dispatchEvent(new CustomEvent('sessionexpired', { detail: sessionEndReason }));

        // Best-effort server-side logout (don't await, don't re-trigger interceptor)
        const wasEviewMode = localStorage.getItem('eviewMode') === 'true';
        const logoutMethod = wasEviewMode ? 'auth.logoutUserEnterprise' : 'auth.logoutUser';
        if (sSess) {
          // Use XMLHttpRequest to avoid triggering our own interceptor
          try {
            const xhr = new XMLHttpRequest();
            const url = response.config.url || '';
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.send(JSON.stringify({
              jsonrpc: 2.0,
              method: logoutMethod,
              id: 200,
              params: [sSess]
            }));
          } catch (_) { /* best effort */ }
        }

        // Navigate to login
        setTimeout(() => {
          history.push('/');
          // Reset cooldown
          setTimeout(() => { _expiring = false; }, EXPIRY_COOLDOWN_MS);
        }, 100);
      }

      // Still return the response so the original caller's .then() runs
      // (it will see noauth and can handle gracefully or ignore since we're redirecting)
      return response;
    },
    // Network errors pass through unchanged
    (error) => Promise.reject(error)
  );
};

/**
 * Reset internal state (for testing only).
 */
export const _resetForTesting = () => {
  _expiring = false;
};
